import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, Sparkles, X, Eye, Users, Trash2, RefreshCw, History, Mail, Globe } from "lucide-react";

const SECTIONS = {
  leads: { title: "Importa Rivenditori (CSV/Excel)", need: "Nome Attività + Sito Web (o Profilo Social / E-mail)", noun: "rivenditori" },
  media: { title: "Importa Testate (CSV/Excel)", need: "Nome Testata + Sito Web (o Profilo Social / E-mail / Telefono-WhatsApp)", noun: "testate" },
  ambassador: { title: "Importa Ambassador (CSV/Excel)", need: "Nome + Profilo Social (o Sito Web / E-mail)", noun: "profili" },
  ambassadors: { title: "Importa Ambassador (CSV/Excel)", need: "Nome + Profilo Social (o Sito Web / E-mail)", noun: "profili" },
};

const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("it-IT") : ""; } catch { return ""; } };
const MATCH_LABEL = { email: "stessa e-mail", sito: "stesso sito", handle: "stesso profilo", anagrafica: "già in anagrafica" };

function DuplicateRow({ d, checked, onToggle }) {
  const r = d.record || {}, m = d.match || {};
  const resolved = d.resolution && d.resolution !== "pending";
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 text-xs ${resolved ? "border-border bg-brema-bg/40 opacity-70" : "border-amber-500/30 bg-amber-500/5"}`} data-testid="import-duplicate-row">
      {!resolved && <Checkbox checked={checked} onCheckedChange={onToggle} data-testid="import-duplicate-checkbox" className="mt-0.5" />}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="font-medium text-foreground" data-testid="import-duplicate-name">{r.name}</span>
          {r.email && <span className="inline-flex items-center gap-1 text-muted-foreground"><Mail className="h-3 w-3" />{r.email}</span>}
          {r.website && <span className="inline-flex items-center gap-1 truncate text-muted-foreground"><Globe className="h-3 w-3" />{r.website}</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          {m.existing_id && (
            <span className="rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300" data-testid="import-duplicate-existing-badge">
              In anagrafica{m.existing_name && m.existing_name !== r.name ? ` come "${m.existing_name}"` : ""} · {(m.matched_on || []).map((k) => MATCH_LABEL[k] || k).join(", ")}
            </span>
          )}
          {m.contacted && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300" data-testid="import-duplicate-contacted-badge">
              <History className="h-3 w-3" /> Già contattato{m.last_sent_at ? ` il ${fmtDate(m.last_sent_at)}` : ""}{m.sent_count > 1 ? ` (${m.sent_count} invii)` : ""}
            </span>
          )}
          {resolved && (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground" data-testid="import-duplicate-resolution">
              {d.resolution === "discarded" ? "Scartato" : d.resolution === "overwritten" ? "Sovrascritto" : "Importato come già contattato"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function ImportContactsDialog({ open, onOpenChange, section, onDone, onViewAll }) {
  const cfg = SECTIONS[section] || SECTIONS.leads;
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const [dupSel, setDupSel] = useState(new Set());
  const [resolving, setResolving] = useState("");
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  useEffect(() => {
    if (open) { setFile(null); setUploading(false); setJob(null); setErr(""); setDupSel(new Set()); setResolving(""); }
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadJob = async (jobId) => {
    const { data: j } = await api.get(`/import-jobs/${jobId}`);
    setJob(j);
    return j;
  };

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file CSV o Excel"); return; }
    setUploading(true);
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/${section}/import`, fd);
      const j = await loadJob(data.job_id);
      setDupSel(new Set((j.duplicates || []).filter((d) => d.resolution === "pending").map((d) => d.row)));
      if (data.imported > 0 && j.status !== "done") {
        pollRef.current = setInterval(async () => {
          try {
            const jj = await loadJob(data.job_id);
            if (jj.status === "done" || jj.status === "error") { stopPoll(); setUploading(false); onDone?.(); }
          } catch { /* keep polling */ }
        }, 2500);
      } else {
        setUploading(false);
        onDone?.();
      }
    } catch (e) {
      setUploading(false);
      const msg = formatApiError(e.response?.data?.detail) || "Import fallito. Controlla il file e riprova.";
      setErr(msg);
      toast.error(msg);
    }
  };

  const resolve = async (action) => {
    if (!job || dupSel.size === 0) return;
    setResolving(action);
    try {
      const { data } = await api.post(`/import-jobs/${job._id}/resolve`, { action, rows: [...dupSel] });
      const n = data.overwritten + data.imported_as_contacted + data.discarded;
      toast.success(action === "discard"
        ? `${data.discarded} ${data.discarded === 1 ? "contatto scartato" : "contatti scartati"} (non importati)`
        : `${n} ${n === 1 ? "contatto sovrascritto" : "contatti sovrascritti"}${data.imported_as_contacted ? ` · ${data.imported_as_contacted} importati come già contattati` : ""}`);
      const j = await loadJob(job._id);
      setDupSel(new Set((j?.duplicates || []).filter((d) => d.resolution === "pending").map((d) => d.row)));
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione fallita"); }
    finally { setResolving(""); }
  };

  const done = job && job.status === "done";
  const running = job && job.status === "running";
  const pct = job && job.imported > 0 ? Math.round(((job.done || 0) / job.imported) * 100) : 0;
  const dups = job?.duplicates || [];
  const pendingDups = dups.filter((d) => d.resolution === "pending");
  const allPendingSel = pendingDups.length > 0 && pendingDups.every((d) => dupSel.has(d.row));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!running) onOpenChange(v); }}>
      <DialogContent data-testid="import-dialog" className="max-h-[90vh] max-w-xl overflow-y-auto border-border bg-brema-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <FileSpreadsheet className="h-5 w-5 text-primary" /> {cfg.title}
          </DialogTitle>
          <DialogDescription>
            Carica un file <b>.csv</b> o <b>.xlsx</b>. Colonne minime richieste per riga: <b>{cfg.need}</b>. Colonne riconosciute: Nome, Sito, E-mail, Telefono/WhatsApp, Instagram, Città, Categoria.
            Ogni contatto viene confrontato con l'anagrafica e con lo Storico Invii (e-mail, sito e/o profilo Instagram): i già esistenti/già contattati
            <b> non vengono reimportati come nuovi</b> e potrai scegliere se scartarli o sovrascriverli. I nuovi vengono arricchiti e salvati con stato <b>Nuovo</b>.
          </DialogDescription>
        </DialogHeader>

        {!job && (
          <div className="space-y-4">
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" data-testid="import-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" />
            <button type="button" data-testid="import-dropzone" onClick={() => fileRef.current?.click()}
              className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border bg-brema-bg/50 px-4 py-8 text-center transition-colors hover:border-primary/50 hover:bg-brema-hover">
              <Upload className="h-7 w-7 text-muted-foreground" />
              {file ? (
                <span className="text-sm font-medium text-foreground">{file.name}</span>
              ) : (
                <>
                  <span className="text-sm font-medium text-foreground">Clicca per scegliere un file</span>
                  <span className="text-xs text-muted-foreground">CSV o Excel (.xlsx)</span>
                </>
              )}
            </button>
            {err && (
              <div data-testid="import-error" className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{err}</span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="import-cancel-btn"
                className="rounded-full border border-border bg-transparent hover:bg-brema-hover">Annulla</Button>
              <Button onClick={upload} disabled={!file || uploading} data-testid="import-upload-btn"
                className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carico…</> : <><Upload className="mr-2 h-4 w-4" /> Importa e arricchisci</>}
              </Button>
            </div>
          </div>
        )}

        {running && (
          <div className="space-y-3 py-2" data-testid="import-progress">
            <div className="flex items-center gap-2 text-sm text-foreground">
              <Sparkles className="h-4 w-4 animate-pulse text-primary" />
              Importati <b>{job.imported}</b> {cfg.noun} nuovi{job.skipped ? ` · ${job.skipped} righe saltate` : ""}{dups.length ? ` · ${dups.length} già esistenti/contattati` : ""} · arricchimento in corso…
            </div>
            <Progress value={pct} className="h-2" />
            <div className="text-xs text-muted-foreground">{job.done || 0}/{job.imported} elaborati · {job.enriched || 0} arricchiti</div>
          </div>
        )}

        {done && (
          <div className="space-y-3 py-2" data-testid="import-summary">
            <div className="flex items-center gap-2 text-base font-semibold text-foreground">
              <CheckCircle2 className="h-5 w-5 text-positive" /> Import completato
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div className="rounded-xl border border-border bg-brema-bg/60 p-3 text-center">
                <div className="text-2xl font-bold text-foreground" data-testid="import-count-imported">{job.imported}</div>
                <div className="text-[11px] text-muted-foreground">Nuovi importati</div>
              </div>
              <div className="rounded-xl border border-positive/30 bg-positive/10 p-3 text-center">
                <div className="text-2xl font-bold text-positive" data-testid="import-count-enriched">{job.enriched}</div>
                <div className="text-[11px] text-muted-foreground">Arricchiti</div>
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-2xl font-bold text-amber-600 dark:text-amber-400" data-testid="import-count-missing">
                  {job.missing > 0 && <AlertTriangle className="h-4 w-4" />}{job.missing}
                </div>
                <div className="text-[11px] text-muted-foreground">Dati mancanti</div>
              </div>
              <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-3 text-center">
                <div className="text-2xl font-bold text-sky-700 dark:text-sky-300" data-testid="import-count-duplicates">{dups.length}</div>
                <div className="text-[11px] text-muted-foreground">Già esistenti</div>
              </div>
            </div>
            {job.skipped > 0 && (
              <p className="text-xs text-muted-foreground">
                <b>{job.skipped}</b> righe saltate (senza Nome o senza Sito/Social/E-mail).
              </p>
            )}
          </div>
        )}

        {job && dups.length > 0 && (
          <div className="space-y-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="import-duplicates-section">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Users className="h-4 w-4 text-amber-600" /> Contatti già esistenti / Già contattati
                <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-bold text-amber-700 dark:text-amber-300" data-testid="import-duplicates-count">{dups.length}</span>
              </div>
              {pendingDups.length > 0 && (
                <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Checkbox checked={allPendingSel} data-testid="import-duplicates-select-all"
                    onCheckedChange={(v) => setDupSel(v ? new Set(pendingDups.map((d) => d.row)) : new Set())} /> Seleziona tutti
                </label>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Trovati confrontando e-mail e sito con l'anagrafica e lo Storico Invii. <b>Non sono stati importati come nuovi</b>: scegli se scartarli o sovrascrivere i dati esistenti con quelli del file (lo storico resta; chi esiste solo nello Storico viene salvato come "contattato").
            </p>
            <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
              {dups.map((d) => (
                <DuplicateRow key={d.row} d={d} checked={dupSel.has(d.row)}
                  onToggle={() => setDupSel((s) => { const n = new Set(s); n.has(d.row) ? n.delete(d.row) : n.add(d.row); return n; })} />
              ))}
            </div>
            {pendingDups.length > 0 ? (
              <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                <span className="mr-auto text-[11px] text-muted-foreground" data-testid="import-duplicates-pending">{pendingDups.length} in sospeso · {dupSel.size} selezionati</span>
                <Button size="sm" variant="secondary" disabled={!!resolving || dupSel.size === 0} onClick={() => resolve("discard")} data-testid="import-duplicates-discard-btn"
                  className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                  {resolving === "discard" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Trash2 className="mr-1.5 h-3.5 w-3.5" />} Scarta ({dupSel.size})
                </Button>
                <Button size="sm" disabled={!!resolving || dupSel.size === 0} onClick={() => resolve("overwrite")} data-testid="import-duplicates-overwrite-btn"
                  className="h-8 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                  {resolving === "overwrite" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Sovrascrivi ({dupSel.size})
                </Button>
              </div>
            ) : (
              <p className="text-[11px] text-positive" data-testid="import-duplicates-resolved">
                Tutti gestiti: {job.duplicates_overwritten || 0} sovrascritti · {job.duplicates_discarded || 0} scartati.
              </p>
            )}
          </div>
        )}

        {done && (
          <div className="flex justify-end gap-2">
            {onViewAll && job.imported > 0 && (
              <Button variant="secondary" onClick={() => { onOpenChange(false); onViewAll(); }} data-testid="import-view-all-btn"
                className="rounded-full border border-border bg-transparent hover:bg-brema-hover">
                <Eye className="mr-2 h-4 w-4" /> Vedi tutti
              </Button>
            )}
            <Button onClick={() => onOpenChange(false)} data-testid="import-close-btn"
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              <X className="mr-2 h-4 w-4" /> Chiudi
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
